import React, { useState, useEffect } from 'react';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import { Button, Dialog, Select, MenuItem } from '@dsi/react-eds';
import ReactDOM from 'react-dom';
import api from '../../../api/axiosInstance';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import { AxiosError } from 'axios';
import { MoshellEndpoint } from './moshell-endpoint-interfaces';

// Type definitions
interface Enm {
  id: string;
  name: string;
  category: string;
}

interface LinkEnmsDialogProps {
  open: boolean;
  moshell: MoshellEndpoint | undefined;
  onSave: (moshellId: string, linkedEnmIds: string[]) => void;
  onClose: () => void;
}

const LinkEnmsDialog: React.FC<LinkEnmsDialogProps> = ({ open, moshell, onSave, onClose }) => {
  const { addNotifications } = useSwobNotifications();
  const [enms, setEnms] = useState<Enm[]>([]);
  const [selectedEnms, setSelectedEnms] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch ENMs when dialog opens
  useEffect(() => {
    if (open && moshell?.id) {
      fetchData();
    }
  }, [open, moshell?.id]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Fetch ENMs
      const [enmsResponse] = await Promise.all([
        api.get('/api/enm'),
      ]);

      const linkedEnmIds = moshell?.moshellEnms?.map(e => e.enm_id) || [];

      console.log('Current linked ENMs:', linkedEnmIds);

      setEnms(enmsResponse.data);
      setSelectedEnms(linkedEnmIds);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while fetching data.',
          timestamp: new Date(),
          color: 'red',
        },
      ]);
    }
  };

  const handleSave = () => {
    onSave(moshell?.id || '', selectedEnms);
  };

  const handleClose = () => {
    setSelectedEnms(moshell?.moshellEnms?.map(e => e.enm_id) || []);
    onClose();
  };

  return ReactDOM.createPortal(
    <ReactEDSWrapper
      component={Dialog}
      className={'overFlowDialog'}
      show={open}
      onClose={handleClose}
      title="Link ENMs"
      subtitle={`Configure ENM links for ${moshell?.name || 'moshell'}`}
      bottom={
        <ReactEDSWrapper
          component={Button}
          primary
          onClick={handleSave}
        >
          Save
        </ReactEDSWrapper>
      }
    >
      {open && (
        <div style={{ padding: '24px' }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <p>Loading data...</p>
            </div>
          ) : (
            <>
              <p style={{ marginBottom: '16px', color: '#6a6a6a' }}>
                Select ENM instances to link with this Moshell Endpoint. Linked ENMs will be associated with the Moshell Endpoint.
              </p>

              <ReactEDSWrapper
                component={Select}
                type="input-multi"
                label="Select ENMs"
                key={`enm-select-${moshell?.id}-${selectedEnms.length}`}
                value={selectedEnms}
                onChange={(event: any) => {
                  const newValue = event?.target?.value ?? event;
                  console.log('Select onChange - newValue:', newValue);
                  setSelectedEnms(Array.isArray(newValue) ? newValue : []);
                }}
              >
                {enms.map((enm) => (
                  <ReactEDSWrapper key={enm.id} component={MenuItem} value={enm.id}>
                    {`${enm.name} (${enm.category})`}
                  </ReactEDSWrapper>
                ))}
              </ReactEDSWrapper>
            </>
          )}
        </div>
      )}
    </ReactEDSWrapper>,
    document.body
  );
};

export default LinkEnmsDialog;
